import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  BarChart, Bar, PieChart, Pie, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import { 
  Users, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Crown,
  Heart,
  Star,
  AlertCircle,
  Target,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Activity,
  Download,
  Shield,
  Clock,
  DollarSign,
  Zap,
  XCircle,
  PauseCircle,
  Moon,
  ZoomIn
} from 'lucide-react';
import axios from 'axios';
import { FormulaTooltip, resolveMetricKey } from '../FormulaTooltip';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';
import DashboardFilters from '../DashboardFilters';
import { formatNumber, formatCurrency } from '../../lib/formatters';
import { ExportButton } from '../ExportUtils';
import { AskAIButton } from '../AskAIButton';
import { LaunchCampaignButton } from '../LaunchCampaignButton';
import AIInsightPanel from '../AIInsightPanel';

import { API_BASE_URL as API } from '../../utils/apiConfig';

const SEGMENT_ICONS = {
  'Champions': Crown,
  'Loyal': Heart,
  'Recent': Sparkles,
  'Potential Loyal': TrendingUp,
  'Promising': Star,
  'Need Attention': AlertTriangle,
  'About to Sleep': Moon,
  "Can't Lose": AlertCircle,
  'At Risk': Shield,
  'Hibernating': PauseCircle,
  'Lost': XCircle
};

// Insight Card with severity styling and campaign activation
const InsightCard = ({ title, insight, recommendation, severity = "info", onDrillDown, brandId, segmentName, customers, onCampaignCreated }) => {
  const severityStyles = {
    critical: { bg: "from-red-500/20 to-red-600/10", border: "border-red-500/30", icon: AlertCircle, iconColor: "text-red-400", priority: "P0" },
    warning: { bg: "from-amber-500/20 to-amber-600/10", border: "border-amber-500/30", icon: AlertTriangle, iconColor: "text-amber-400", priority: "P1" },
    success: { bg: "from-emerald-500/20 to-emerald-600/10", border: "border-emerald-500/30", icon: TrendingUp, iconColor: "text-emerald-400", priority: "P2" },
    info: { bg: "from-blue-500/20 to-blue-600/10", border: "border-blue-500/30", icon: Sparkles, iconColor: "text-blue-400", priority: "P2" }
  };
  
  const style = severityStyles[severity] || severityStyles.info;
  const Icon = style.icon;
  
  return (
    <Card 
      className={`bg-gradient-to-br ${style.bg} ${style.border} border hover:shadow-lg transition-all group`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-background/50 ${style.iconColor}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm mb-1">{title}</h4>
            <p className="text-xs text-muted-foreground mb-2">{insight}</p>
            {recommendation && (
              <p className="text-xs text-foreground/80 bg-background/30 p-2 rounded mb-2">
                <strong>Action:</strong> {recommendation}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 text-xs gap-1"
                onClick={onDrillDown}
              >
                <ZoomIn className="w-3 h-3" />
                View Details
              </Button>
              {brandId && recommendation && (
                <LaunchCampaignButton
                  segmentName={segmentName || title}
                  customers={customers || 0}
                  brandId={brandId}
                  recommendation={{
                    action: recommendation,
                    priority: style.priority
                  }}
                  onCampaignCreated={onCampaignCreated}
                  size="sm"
                  className="h-7 text-xs bg-violet-500 hover:bg-violet-600"
                  buttonText="Activate"
                />
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Segment Card with drill-down
const SegmentCard = ({ segment, onClick }) => {
  const Icon = SEGMENT_ICONS[segment.name] || Users;
  
  // Use imported formatNumber and formatCurrency from lib/formatters

  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all group"
      onClick={() => onClick(segment)}
      data-testid={`segment-${segment.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div 
              className="p-2 rounded-lg" 
              style={{ backgroundColor: `${segment.color}20` }}
            >
              <Icon className="w-4 h-4" style={{ color: segment.color }} />
            </div>
            <div>
              <h4 className="font-semibold text-sm truncate">{segment.name}</h4>
              <p className="text-xs text-muted-foreground truncate">{segment.action}</p>
            </div>
          </div>
          <span 
            className="text-xs px-2 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: `${segment.color}20`, color: segment.color }}
          >
            {segment.avg_churn}% churn
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-2 rounded bg-muted/50 text-center">
            <p className="text-lg font-bold truncate">{formatNumber(segment.customers)}</p>
            <p className="text-xs text-muted-foreground">{segment.customers_pct}%</p>
          </div>
          <div className="p-2 rounded bg-muted/50 text-center">
            <p className="text-lg font-bold truncate">{formatCurrency(segment.revenue)}</p>
            <p className="text-xs text-muted-foreground">{segment.revenue_pct}%</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Avg: {formatCurrency(segment.avg_revenue)}</span>
          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </CardContent>
    </Card>
  );
};

// KPI Card
const KPICard = ({ label, value, subtext, icon: Icon, color, trend, onClick }) => (
  <Card 
    className="cursor-pointer hover:shadow-lg transition-all group"
    onClick={onClick}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-xl`} style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">{label}</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold">{value}</p>
            {trend && (
              <span className={`text-xs ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
              </span>
            )}
          </div>
          {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
      </div>
    </CardContent>
  </Card>
);

// Exportable List Card
const ExportCard = ({ list, onExport }) => {
  // Use imported formatNumber from lib/formatters

  return (
    <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => onExport(list)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm truncate">{list.name}</h4>
            <p className="text-xs text-muted-foreground truncate">{list.description}</p>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <span className="text-lg font-bold">{formatNumber(list.count)}</span>
            <Download className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function RFMChurnDashboard() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [isLiveData, setIsLiveData] = useState(false);
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });

  // Chart zoom handlers
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({ open: true, type: chartType, data: chartData, title: chartTitle, config: chartConfig });
  };
  const closeChartZoom = () => setChartZoom({ open: false, type: '', data: [], title: '', config: {} });
  const [filters, setFilters] = useState({ dateRange: 'lifetime', city: 'all', store: 'all' });
  const [filterOptions, setFilterOptions] = useState({ cities: [], stores: [] });

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    setError(null);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    // Add cache-busting to ensure fresh data
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Use original API for proper data structure
    try {
      const response = await axios.post(
        `${API}/api/analytics/rfm-churn?_t=${Date.now()}&_rid=${requestId}`,
        { brand_id: selectedBrand.brand_id, filters },
        { 
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'X-Request-ID': requestId
          }
        }
      );
      
      // Force new object reference
      setData({...response.data.data});
      // Set live status from response or brand settings
      setIsLiveData(response.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      
      // Extract filter options from response
      if (response.data.data?.filter_context) {
        setFilterOptions({
          cities: response.data.data.filter_context.cities || [],
          stores: response.data.data.filter_context.stores || []
        });
      }
    } catch (err) {
      console.error('RFM Churn data error:', err);
      setError('Failed to load RFM & Churn data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, getDbBrandId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrillDown = (type, value, title, context = {}) => {
    setDrillDown({ open: true, type, data: { value, context }, title });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  const handleSegmentClick = (segment) => {
    openDrillDown('rfm_segment', segment.name, `${segment.name} Segment Details`, { segment });
  };

  const handleExportList = (list) => {
    // In production, this would trigger a download
    openDrillDown('export_list', list.name, `Export: ${list.name}`, { list });
  };

  const handleExport = () => {
    if (!data) return;
    const csvContent = [
      ['Segment', 'Customers', 'Revenue', 'Avg Revenue', 'Churn Risk', 'Action'],
      ...(data.segments || []).map(s => [s.name, s.customers, s.revenue, s.avg_revenue, s.avg_churn, s.action])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rfm_churn_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="rfm-churn-loading">
        <div className="flex flex-col items-center gap-4">
          <Target className="w-12 h-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Analyzing RFM & Churn data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const segments = data?.segments || [];
  const churnDistribution = data?.churn_distribution || [];
  const revenueAtRisk = data?.revenue_at_risk || {};
  const recencyDistribution = data?.recency_distribution || [];
  const rfmHeatmap = data?.rfm_heatmap || [];
  const insights = data?.insights || {};
  const exportableLists = data?.exportable_lists || {};

  // N/A state — RFM not yet computed for this brand
  if (data?.available === false) {
    return (
      <div className="space-y-6" data-testid="rfm-churn-na">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
              <Target className="w-6 h-6 text-white" />
            </div>
            RFM & Churn Prediction
          </h2>
        </div>
        <Card className="border-amber-500/40 bg-amber-500/10 p-6 max-w-3xl">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-500/20 rounded">
              <Target className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-amber-200">RFM data not available — N/A</h3>
              <p className="text-sm text-amber-100/90 mt-2">
                {data?.na_reason || 'RFM segments have not been computed for this brand yet.'}
              </p>
              <p className="text-xs text-amber-100/60 mt-3">
                Real RFM uses actual customer recency, frequency, monetary scores from raw transactions — never hardcoded percentages.
                Visit <strong>Data Quality</strong> in the sidebar and click "Compute Now" on the RFM finding for this brand.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rfm-churn-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
              <Target className="w-6 h-6 text-white" />
            </div>
            RFM & Churn Prediction
            {isLiveData && (
              <Badge className="ml-2 bg-emerald-500 text-white animate-pulse">
                <Zap className="w-3 h-3 mr-1" />
                LIVE
              </Badge>
            )}
            {data?.data_as_of && (
              <Badge className="ml-2 bg-blue-500/80 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Data as of: {data.data_as_of}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground mt-1">
            {isLiveData 
              ? `Live data: ${data?.summary?.total_customers?.toLocaleString() || 0} customers`
              : 'Customer segmentation with churn risk analysis'
            }
          </p>
        </div>
      </div>

      {/* Dashboard Filters */}
      <DashboardFilters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        onRefresh={fetchData}
        onExport={handleExport}
        loading={loading}
      />

      {/* Full Report Export Button */}
      <div className="flex justify-end">
        <ExportButton 
          brandId={selectedBrand?.brand_id}
          dashboardType="rfm_churn"
          label="Download Full Report"
          filename={`rfm_churn_${selectedBrand?.name || 'export'}.csv`}
        />
      </div>

      {/* Real GPT-5.2 AI Insight (cached 24h) */}
      <AIInsightPanel
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name || selectedBrand?.brand_name}
        kpiName="rfm_churn_summary"
        context={{
          total_customers: summary.total_customers,
          total_revenue: summary.total_revenue,
          avg_rfm_score: summary.avg_rfm_score,
          avg_churn_probability: summary.avg_churn_probability,
          champions_pct: summary.champions_pct,
          at_risk_pct: summary.at_risk_pct,
          top_segments: (segments || []).slice(0, 5).map(s => ({
            name: s.name,
            customers: s.customers,
            customers_pct: s.customers_pct,
            revenue: s.revenue,
            revenue_pct: s.revenue_pct,
            avg_recency_days: s.avg_recency_days,
            avg_frequency: s.avg_frequency,
          })),
          churn_distribution: (churnDistribution || []).map(c => ({ band: c.band, count: c.count, revenue: c.revenue })),
          revenue_at_risk: revenueAtRisk,
        }}
      />

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          label="Total Customers"
          value={summary.total_customers?.toLocaleString()}
          subtext="Analyzed"
          icon={Users}
          color="#8B5CF6"
          onClick={() => openDrillDown('kpi', 'total_customers', 'Customer Distribution')}
        />
        <KPICard
          label="Total Revenue"
          value={formatCurrency(summary.total_revenue)}
          icon={DollarSign}
          color="#10B981"
          onClick={() => openDrillDown('kpi', 'revenue', 'Revenue by Segment')}
        />
        <KPICard
          label="Avg RFM Score"
          value={summary.avg_rfm_score?.toFixed(1)}
          subtext="out of 5"
          icon={Activity}
          color="#3B82F6"
          onClick={() => openDrillDown('kpi', 'rfm_score', 'RFM Score Distribution')}
        />
        <KPICard
          label="Champions"
          value={`${summary.champions_pct}%`}
          subtext="of customers"
          icon={Crown}
          color="#F59E0B"
          onClick={() => openDrillDown('rfm_segment', 'Champions', 'Champions Analysis')}
        />
        <KPICard
          label="At Risk"
          value={`${summary.at_risk_pct}%`}
          subtext="need attention"
          icon={AlertTriangle}
          color="#EF4444"
          onClick={() => openDrillDown('kpi', 'at_risk', 'At Risk Analysis')}
        />
        <KPICard
          label="Avg Churn Prob"
          value={`${summary.avg_churn_probability}%`}
          icon={TrendingDown}
          color="#F97316"
          onClick={() => openDrillDown('kpi', 'churn', 'Churn Probability Distribution')}
        />
      </div>

      {/* Revenue at Risk Alert */}
      <Card className="bg-gradient-to-r from-red-500/10 via-orange-500/5 to-amber-500/10 border-red-500/20">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-red-500/20">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Revenue at Risk</h3>
                <p className="text-sm text-muted-foreground">
                  High and Critical churn bands represent significant revenue exposure
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 rounded-lg bg-red-500/10">
                <p className="text-2xl font-bold text-red-400">{formatCurrency(revenueAtRisk.critical)}</p>
                <p className="text-xs text-muted-foreground">Critical Risk</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-orange-500/10">
                <p className="text-2xl font-bold text-orange-400">{formatCurrency(revenueAtRisk.high)}</p>
                <p className="text-xs text-muted-foreground">High Risk</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-amber-500/10">
                <p className="text-2xl font-bold text-amber-400">{formatCurrency(revenueAtRisk.total_at_risk)}</p>
                <p className="text-xs text-muted-foreground">Total at Risk</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Insights Row */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InsightCard
          title={insights.champions?.title}
          insight={insights.champions?.insight}
          recommendation={insights.champions?.recommendation}
          severity={insights.champions?.severity}
          onDrillDown={() => openDrillDown('rfm_segment', 'Champions', 'Champions Deep Dive')}
        />
        <InsightCard
          title={insights.revenue_at_risk?.title}
          insight={insights.revenue_at_risk?.insight}
          recommendation={insights.revenue_at_risk?.recommendation}
          severity={insights.revenue_at_risk?.severity}
          onDrillDown={() => openDrillDown('kpi', 'revenue_at_risk', 'Revenue at Risk Deep Dive')}
        />
        <InsightCard
          title={insights.recency?.title}
          insight={insights.recency?.insight}
          recommendation={insights.recency?.recommendation}
          severity={insights.recency?.severity}
          onDrillDown={() => openDrillDown('kpi', 'recency', 'Recency Analysis')}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="segments">RFM Segments</TabsTrigger>
          <TabsTrigger value="churn">Churn Analysis</TabsTrigger>
          <TabsTrigger value="export">Export Lists</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Churn Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  Churn Risk Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={churnDistribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="band" width={70} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'count' ? value?.toLocaleString() : formatCurrency(value),
                        name === 'count' ? 'Customers' : 'Revenue'
                      ]}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="count" name="Customers" radius={[0, 4, 4, 0]}>
                      {churnDistribution.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Recency Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  Days Since Last Visit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={recencyDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(val) => val >= 100000 ? `${(val/100000).toFixed(0)}L` : val >= 1000 ? `${(val/1000).toFixed(0)}K` : val} />
                    <Tooltip 
                      formatter={(value) => {
                        const formatted = value >= 100000 ? `${(value/100000).toFixed(1)}L` : value >= 1000 ? `${(value/1000).toFixed(0)}K` : value?.toLocaleString();
                        return [formatted, 'Customers'];
                      }}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                      {recencyDistribution.map((entry, idx) => (
                        <Cell 
                          key={idx} 
                          fill={
                            entry.range === '0-30' ? '#10B981' :
                            entry.range === '31-60' ? '#14B8A6' :
                            entry.range === '61-90' ? '#3B82F6' :
                            entry.range === '91-180' ? '#F59E0B' :
                            entry.range === '181-365' ? '#F97316' :
                            '#EF4444'
                          } 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* RFM Heatmap */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-violet-500" />
                RFM Heatmap (Recency vs Frequency)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    type="number" 
                    dataKey="r" 
                    name="Recency Score" 
                    domain={[0, 6]}
                    tick={{ fontSize: 11 }}
                    label={{ value: 'Recency Score (5=Most Recent)', position: 'bottom', fontSize: 11 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="f" 
                    name="Frequency Score" 
                    domain={[0, 6]}
                    tick={{ fontSize: 11 }}
                    label={{ value: 'Frequency Score', angle: -90, position: 'left', fontSize: 11 }}
                  />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(value, name) => [
                      name === 'count' ? value?.toLocaleString() : formatCurrency(value),
                      name === 'count' ? 'Customers' : 'Revenue'
                    ]}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Scatter 
                    data={rfmHeatmap} 
                    fill="#8B5CF6"
                  >
                    {rfmHeatmap.map((entry, idx) => (
                      <Cell 
                        key={idx} 
                        fill={
                          entry.r >= 4 && entry.f >= 4 ? '#10B981' :
                          entry.r >= 3 && entry.f >= 3 ? '#3B82F6' :
                          entry.r <= 2 && entry.f >= 3 ? '#EF4444' :
                          entry.r <= 2 ? '#F97316' :
                          '#8B5CF6'
                        }
                        r={Math.min(Math.sqrt(entry.count) / 10 + 5, 25)}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-2 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span>Champions (High R, High F)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span>Loyal</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Can't Lose (Low R, High F)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span>At Risk</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {segments.map((segment) => (
              <SegmentCard
                key={segment.name}
                segment={segment}
                onClick={handleSegmentClick}
              />
            ))}
          </div>

          {/* Segment Insights */}
          <div className="grid md:grid-cols-2 gap-4">
            <InsightCard
              title={insights.segments_action?.title}
              insight={insights.segments_action?.insight}
              recommendation={insights.segments_action?.recommendation}
              severity={insights.segments_action?.severity}
              onDrillDown={() => openDrillDown('kpi', 'segments', 'Segment Action Priority')}
            />
            <InsightCard
              title={insights.frequency_opportunity?.title}
              insight={insights.frequency_opportunity?.insight}
              recommendation={insights.frequency_opportunity?.recommendation}
              severity={insights.frequency_opportunity?.severity}
              onDrillDown={() => openDrillDown('kpi', 'frequency', 'Frequency Opportunity')}
            />
          </div>
        </TabsContent>

        {/* Churn Analysis Tab */}
        <TabsContent value="churn" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {churnDistribution.map((band) => (
              <Card 
                key={band.band}
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => openDrillDown('churn_band', band.band, `${band.band} Churn Risk`)}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span 
                      className="text-sm font-semibold px-3 py-1 rounded-full"
                      style={{ backgroundColor: `${band.color}20`, color: band.color }}
                    >
                      {band.band}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {band.band === 'Critical' ? '>70%' :
                       band.band === 'High' ? '50-70%' :
                       band.band === 'Medium' ? '30-50%' : '<30%'} risk
                    </span>
                  </div>
                  <p className="text-3xl font-bold mb-1">{band.count?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">customers</p>
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Revenue: </span>
                      <span className="font-semibold">{formatCurrency(band.revenue)}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Churn Heatmap by Segment */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Churn Risk by RFM Segment</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={segments}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Avg Churn Risk']}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="avg_churn" name="Avg Churn %" radius={[4, 4, 0, 0]}>
                    {segments.map((segment, idx) => (
                      <Cell key={idx} fill={segment.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Lists Tab */}
        <TabsContent value="export" className="space-y-6">
          <Card className="bg-gradient-to-r from-violet-500/10 via-pink-500/5 to-blue-500/10 border-violet-500/20">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Targetable Audience Lists</h3>
                  <p className="text-sm text-muted-foreground">
                    Export pre-built customer segments for campaigns, CRM integration, or marketing automation.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(exportableLists).map(([key, list]) => (
              <ExportCard key={key} list={list} onExport={handleExport} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Overall AI Insight */}
      <Card className="bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-blue-500/10 border-emerald-500/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">{insights.overall?.title}</h3>
              <p className="text-sm text-muted-foreground">{insights.overall?.insight}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deep AI Insights */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="rfm_churn"
        dashboardData={data}
        kpis={{
          total_customers: { value: data?.totals?.total_customers || 0 },
          total_revenue: { value: data?.totals?.total_revenue || 0 }
        }}
        segments={data?.segments || []}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="rfm_churn"
      />
    </div>
  );
}
